import type { SVGProps } from 'react';

/**
 * The icon set, traced from the supplied design's inline SVGs rather than pulled from an
 * icon package: the design ships exactly these twenty-odd glyphs, and a library would put
 * a tree-shaking question into the reader-facing bundle for no gain.
 *
 * Each entry is the *contents* of a 24×24 viewBox. Stroke icons inherit `currentColor`
 * and a 1.7 weight, matching the rail; filled brand marks (the social logos) set their
 * own fill. PRD §8 lets an editor pick a category icon by name, and `categoryIcon()`
 * below is the lookup that turns that name into one of these.
 */
export type IconName =
  | 'home'
  | 'state'
  | 'national'
  | 'local'
  | 'sports'
  | 'health'
  | 'jobs'
  | 'entertainment'
  | 'videos'
  | 'business'
  | 'technology'
  | 'culture'
  | 'search'
  | 'moon'
  | 'menu'
  | 'close'
  | 'chevron-right'
  | 'chevron-left'
  | 'arrow-right'
  | 'arrow-up'
  | 'clock'
  | 'share'
  | 'link'
  | 'mail'
  | 'phone'
  | 'pin'
  | 'play'
  | 'print'
  | 'bookmark'
  | 'facebook'
  | 'x'
  | 'instagram'
  | 'youtube'
  | 'whatsapp';

const STROKE: Partial<Record<IconName, JSX.Element>> = {
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </>
  ),
  state: (
    // The design's STATE card: a civic building, not the rail's filing-cabinet glyph.
    <path d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6" />
  ),
  national: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </>
  ),
  local: (
    <>
      <path d="M12 21s-7-4.6-7-10a7 7 0 0 1 14 0c0 5.4-7 10-7 10Z" />
      <circle cx="12" cy="11" r="2.4" />
    </>
  ),
  sports: (
    <>
      <circle cx="7" cy="16" r="3" />
      <circle cx="17" cy="16" r="3" />
      <path d="M10 16h4M7 13V7l6-2v8" />
    </>
  ),
  health: <path d="M20 8.5a4 4 0 0 0-7-2.6A4 4 0 0 0 6 8.5c0 4 6 8 6 8s6-4 6-8Z" />,
  jobs: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="1.5" />
      <path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7" />
    </>
  ),
  entertainment: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m10 8 6 4-6 4Z" />
    </>
  ),
  videos: (
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3Z" />
    </>
  ),
  business: (
    <>
      <path d="M4 19V9M10 19V5M16 19v-6M22 19H2" />
    </>
  ),
  technology: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 9h6v6H9zM9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  culture: (
    <>
      <path d="M12 3 4 7v6c0 4.4 3.4 7.4 8 8 4.6-.6 8-3.6 8-8V7Z" />
      <path d="m9.5 12 1.8 1.8L15 10" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />,
  close: <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />,
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  'chevron-left': <path d="M15 6l-6 6 6 6" />,
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  'arrow-up': <path d="M12 19V5M6 11l6-6 6 6" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.4 2" strokeLinecap="round" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 10.6 6.8-4M8.6 13.4l6.8 4" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.5.5l2-2A5 5 0 0 0 12.5 4.5l-1.2 1.1" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2A5 5 0 0 0 11.5 19.5l1.2-1.1" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  phone: (
    <path d="M6 3h3l2 5-2.5 1.5a11 11 0 0 0 5 5L15 12l5 2v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4 5.2 2 2 0 0 1 6 3Z" />
  ),
  print: (
    <>
      <path d="M7 9V4h10v5" />
      <rect x="4" y="9" width="16" height="7" rx="1.5" />
      <path d="M7 14h10v6H7z" />
    </>
  ),
  bookmark: <path d="M7 4h10v16l-5-3.5L7 20Z" />,
  pin: (
    <>
      <path d="M12 21s-7-4.6-7-10a7 7 0 0 1 14 0c0 5.4-7 10-7 10Z" />
      <circle cx="12" cy="11" r="2.4" />
    </>
  ),
};

const FILLED: Partial<Record<IconName, JSX.Element>> = {
  play: <path d="M8 5.5v13l11-6.5Z" />,
  facebook: (
    <path d="M13 22v-8h3l.5-3H13V9c0-.9.3-1.5 1.6-1.5H16.5V4.8C16.2 4.8 15.2 4.7 14 4.7c-2.4 0-4 1.5-4 4.2V11H7v3h3v8Z" />
  ),
  x: <path d="M18 3h3l-7 8 8 10h-6l-4.6-6L6 21H3l7.4-8.5L3 3h6l4.2 5.6Z" />,
  youtube: (
    <path d="M22 8.2a3 3 0 0 0-2.1-2.1C18 5.6 12 5.6 12 5.6s-6 0-7.9.5A3 3 0 0 0 2 8.2 31 31 0 0 0 2 12a31 31 0 0 0 .1 3.8 3 3 0 0 0 2.1 2.1c1.9.5 7.8.5 7.8.5s6 0 7.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 22 12a31 31 0 0 0-.1-3.8ZM10 15V9l5 3Z" />
  ),
  whatsapp: (
    <path d="M12 2a10 10 0 0 0-8.6 15L2 22l5.2-1.4A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.7-.1a12 12 0 0 1-6.9-6c-.5-.9-.8-1.8-.8-2.6 0-.9.5-1.4.7-1.6.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2 0 .4-.1.5l-.4.5c-.1.1-.3.3-.1.6a9 9 0 0 0 4 3.4c.3.1.5.1.7-.1l.8-1c.2-.2.3-.2.6-.1l2 1c.3.1.4.2.5.3 0 .1 0 .7-.2 1.3Z" />
  ),
};

/** Instagram mixes stroke and fill, so it does not fit either map cleanly. */
const INSTAGRAM = (
  <>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </>
);

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...props }: IconProps) {
  const filled = FILLED[name];
  const shared = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    // Icons are decoration beside a label almost everywhere; the few standalone ones pass
    // their own `aria-label` and `role="img"`, which overrides this.
    'aria-hidden': true,
    focusable: false,
    ...props,
  } as const;

  if (filled) {
    return (
      <svg {...shared} fill="currentColor">
        {filled}
      </svg>
    );
  }

  return (
    <svg {...shared} fill="none" stroke="currentColor" strokeWidth={1.7}>
      {name === 'instagram' ? INSTAGRAM : STROKE[name]}
    </svg>
  );
}

/**
 * PRD §8: "assign icons" to a category in the CMS. The stored value is a lucide-style
 * name (the Phase 2 seed uses `landmark`, `flag`, `map-pin`, …), so this maps the ones the
 * seed actually uses onto the design's own set and falls back to a neutral glyph rather
 * than rendering nothing.
 */
const CATEGORY_ICONS: Record<string, IconName> = {
  landmark: 'state',
  flag: 'national',
  globe: 'national',
  'map-pin': 'local',
  trophy: 'sports',
  'heart-pulse': 'health',
  heart: 'health',
  briefcase: 'jobs',
  film: 'entertainment',
  clapperboard: 'entertainment',
  video: 'videos',
  'trending-up': 'business',
  cpu: 'technology',
  palette: 'culture',
};

export function categoryIcon(name: string | null | undefined): IconName {
  if (!name) return 'state';
  return CATEGORY_ICONS[name] ?? 'state';
}
